import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import styles from './AppointmentDetailsModal.module.css';
import { StaffAppointmentDetailDto } from '../../../types/api-responses';
import { AppointmentStatus } from '../../../types/enums';
// AB#1901 — Use the central parser instead of the local string-then-int ladder.
// BE wire shape is the string ("Pending"/"Approved"/…); parser collapses both
// shapes to the integer enum so the existing isPending/isCompleted/isCancelled
// checks below still read naturally.
import { parseAppointmentStatus } from '../../../lib/enumParsers';
import CancelAppointmentModal from '../CancelAppointmentModal';

interface AppointmentDetailsModalProps {
    isOpen: boolean;
    appointment: StaffAppointmentDetailDto;
    onClose: () => void;
    onStatusUpdate: (id: number, status: AppointmentStatus) => Promise<void>;
    onConfirmAppointment?: (id: number) => Promise<void>;
    onCancelAppointment?: (id: number, reason?: string) => Promise<void>;
    onRescheduleAppointment?: (id: number, newDateTime: string) => Promise<void>;
}

// Map status labels to enum values
const statusOptions = [
    { label: 'Confirmed', value: AppointmentStatus.Confirmed },
    { label: 'Completed', value: AppointmentStatus.Completed },
    { label: 'Cancelled', value: AppointmentStatus.Cancelled }
];

const AppointmentDetailsModal: React.FC<AppointmentDetailsModalProps> = ({
    isOpen,
    appointment,
    onClose,
    onStatusUpdate,
    onConfirmAppointment,
    onCancelAppointment,
    onRescheduleAppointment
}) => {
    const [selectedStatusLabel, setSelectedStatusLabel] = useState<string>('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [validationError, setValidationError] = useState<string>('');
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSelectedStatusLabel('');
            setValidationError('');
            setIsUpdating(false);
            setIsCancelModalOpen(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    // AB#1901 — Determine current status via the central boundary parser.
    // BE wire shape is the string ("Pending"/"Approved"/"Completed"/…); the
    // parser collapses both string and legacy-int forms to the integer enum.
    const currentStatusInt = parseAppointmentStatus(appointment.status);

    const isPending = currentStatusInt === AppointmentStatus.Pending;
    const isCompleted = currentStatusInt === AppointmentStatus.Completed;
    const isCancelled = currentStatusInt === AppointmentStatus.Cancelled;
    const canCancel = !isCompleted && !isCancelled;
    const canReschedule = !isCompleted && !isCancelled;

    const handleStatusChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            setSelectedStatusLabel(value);
            setValidationError('');
        }
    };

    const handleUpdateClick = async () => {
        if (!selectedStatusLabel || selectedStatusLabel === 'Select a status') {
            setValidationError('Please select a status before updating.');
            return;
        }

        const selectedOption = statusOptions.find(s => s.label === selectedStatusLabel);
        if (!selectedOption) return;

        setIsUpdating(true);
        try {
            await onStatusUpdate(appointment.id, selectedOption.value);
        } catch (error) {
            // Error is handled by parent
        } finally {
            setIsUpdating(false);
        }
    };

    const handleConfirmClick = async () => {
        if (!onConfirmAppointment) return;

        setIsUpdating(true);
        try {
            await onConfirmAppointment(appointment.id);
        } catch (error) {
            // Error is handled by parent
        } finally {
            setIsUpdating(false);
        }
    };

    const handleCancelConfirm = async (id: number, reason?: string) => {
        if (!onCancelAppointment) return;

        try {
            await onCancelAppointment(id, reason);
            setIsCancelModalOpen(false);
            onClose();
        } catch (error) {
            // Error is handled by parent
        }
    };

    // Reschedule is handled in a separate modal component.

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !isUpdating) {
            onClose();
        }
    };

    // Determine which status options to show based on current status.
    // AB#1901 — Compare against the normalized integer enum value (see
    // currentStatusInt above). The BE wire string ("Pending"/"Approved"/…)
    // is collapsed by parseAppointmentStatus on intake.
    const getAvailableStatusLabels = () => {
        const options = ['Select a status'];

        if (isPending) {
            options.push('Confirmed', 'Completed', 'Cancelled');
        } else if (currentStatusInt === AppointmentStatus.Confirmed) {
            options.push('Completed', 'Cancelled');
        } else if (currentStatusInt == null) {
            // If status is unknown, allow all transitions.
            options.push('Confirmed', 'Completed', 'Cancelled');
        }
        // Completed and Cancelled appointments cannot be transitioned further.

        return options;
    };

    const availableStatusLabels = getAvailableStatusLabels();
    const canUpdateStatus = availableStatusLabels.length > 1;

    return (
        <>
            <div className={`${styles.modalBackground} ${isOpen ? styles.open : ''}`} onClick={handleOverlayClick}>
                <div className={`${styles.modalContainer} ${isOpen ? styles.open : ''}`}>
                    {isUpdating && (
                        <div className={styles.loadingOverlay}>
                            <div className={styles.loadingContent}>
                                <div className={styles.spinner}></div>
                                <span>Updating...</span>
                            </div>
                        </div>
                    )}

                    <div className={styles.headerCloseContainer}>
                        <button
                            className={styles.closeButton}
                            onClick={onClose}
                            disabled={isUpdating}
                        >
                            &times;
                        </button>
                        <h2 className={`${styles.header} headerText`}>
                            Appointment Details
                        </h2>
                        <p className={styles.subHeader}>
                            Manage appointment status for {appointment.customerName}
                        </p>
                    </div>

                    {/* Quick Action Buttons */}
                    {(isPending || canCancel || canReschedule) && (
                        <div className={styles.quickActions}>
                            {isPending && onConfirmAppointment && (
                                <Button
                                    label="Confirm Appointment"
                                    onClick={handleConfirmClick}
                                    variant="primary"
                                    size="small"
                                    disabled={isUpdating}
                                />
                            )}
                            {onRescheduleAppointment && (
                                <Button
                                    label="Reschedule"
                                    onClick={() => {
                                        if (appointment) {
                                            onRescheduleAppointment(appointment.id, '');
                                        }
                                    }}
                                    variant="secondary"
                                    size="small"
                                    disabled={isUpdating}
                                />
                            )}
                            {canCancel && onCancelAppointment && (
                                <Button
                                    label="Cancel Appointment"
                                    onClick={() => setIsCancelModalOpen(true)}
                                    size="small"
                                    backgroundColor="var(--destructive-fill)"
                                    fontColor="var(--on-destructive)"
                                    disabled={isUpdating}
                                />
                            )}
                        </div>
                    )}

                    {canUpdateStatus ? (
                        <div className={styles.statusSection}>
                            <p className={styles.sectionLabel}>Or update status manually:</p>
                            <Dropdown
                                label="Status"
                                options={availableStatusLabels}
                                value={selectedStatusLabel || 'Select a status'}
                                onChange={handleStatusChange}
                                disabled={isUpdating}
                            />
                            {validationError && (
                                <p className={styles.validationMessage}>{validationError}</p>
                            )}
                        </div>
                    ) : (
                        <div className={styles.statusSection}>
                            <p className={styles.noUpdateMessage}>
                                This appointment status cannot be changed.
                            </p>
                        </div>
                    )}

                    <div className={styles.buttonContainer}>
                        <Button
                            label="Close"
                            onClick={onClose}
                            noAppearance={true}
                            size='small'
                            disabled={isUpdating}
                        />
                        {canUpdateStatus && (
                            <Button
                                label={isUpdating ? 'Updating...' : 'Update Status'}
                                onClick={handleUpdateClick}
                                variant="primary"
                                size='small'
                                disabled={!selectedStatusLabel || selectedStatusLabel === 'Select a status' || isUpdating}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Cancel Appointment Confirmation Modal */}
            <CancelAppointmentModal
                isOpen={isCancelModalOpen}
                appointmentId={appointment.id}
                customerName={appointment.customerName}
                onClose={() => setIsCancelModalOpen(false)}
                onConfirmCancel={handleCancelConfirm}
            />

            {/* Reschedule flow handled by a standalone modal component. */}
        </>
    );
};

export default AppointmentDetailsModal;

